import React, { useState, useEffect } from 'react';
import { Users, CheckCircle2, Clock, Heart, Utensils, Footprints, Lightbulb, Camera, MonitorOff, Sparkles } from 'lucide-react';
import { apiClient } from '../../api/client';

const ICON_MAP = {
  utensils: Utensils,
  footprints: Footprints,
  lightbulb: Lightbulb,
  heart: Heart,
  'monitor-off': MonitorOff,
  sparkles: Sparkles,
  camera: Camera,
};

const CATEGORY_COLORS = {
  connection: { bg: '#f0fdf4', text: '#16a34a', accent: '#22c55e' },
  quality_time: { bg: '#eff6ff', text: '#2563eb', accent: '#3b82f6' },
  learning: { bg: '#fefce8', text: '#ca8a04', accent: '#eab308' },
  appreciation: { bg: '#fdf2f8', text: '#db2777', accent: '#ec4899' },
  presence: { bg: '#f5f3ff', text: '#7c3aed', accent: '#8b5cf6' },
  vulnerability: { bg: '#fff7ed', text: '#ea580c', accent: '#f97316' },
  nostalgia: { bg: '#faf5ff', text: '#9333ea', accent: '#a855f7' },
};

const FamilyChallengeCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [reflection, setReflection] = useState('');
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    apiClient.request('/api/enhancements/family-challenge')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!data?.seconds_until_new) return;
    const end = Date.now() + data.seconds_until_new * 1000;
    const tick = () => {
      const r = Math.max(0, end - Date.now());
      const d = Math.floor(r / 86400000);
      const h = Math.floor((r % 86400000) / 3600000);
      setCountdown(d > 0 ? `${d}d ${h}h` : `${h}h`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [data]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await apiClient.request('/api/enhancements/family-challenge/confirm', {
        method: 'POST',
        body: JSON.stringify({ reflection }),
      });
      setData(prev => ({
        ...prev,
        my_confirmed: true,
        both_completed: res.status === 'completed',
      }));
      setReflection('');
    } catch (e) {
      alert(e.message || 'Failed to confirm');
    } finally { setConfirming(false); }
  };

  if (loading) {
    return (
      <div data-testid="family-challenge-card" className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-44 mb-4" />
        <div className="h-32 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!data) return null;

  const ch = data.challenge;
  const colors = CATEGORY_COLORS[ch.category] || CATEGORY_COLORS.connection;
  const Icon = ICON_MAP[ch.icon] || Heart;

  return (
    <div data-testid="family-challenge-card" className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: colors.bg, color: colors.text }}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Family Challenge</h3>
              <p className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {countdown} left</p>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: colors.bg, color: colors.text }}>
            +{ch.hearts_reward} Hearts each
          </span>
        </div>

        <div className="p-4 rounded-xl mb-4" style={{ background: colors.bg }}>
          <h4 className="font-semibold text-gray-900 mb-1">{ch.title}</h4>
          <p className="text-sm text-gray-600">{ch.description}</p>
        </div>

        {!data.has_family && (
          <p className="text-sm text-gray-400 text-center py-3">Link with a parent or teen to unlock Family Challenges!</p>
        )}

        {data.has_family && !data.both_completed && (
          <>
            {/* Status indicators */}
            <div className="flex gap-3 mb-4">
              <div className={`flex-1 p-3 rounded-xl border ${data.my_confirmed ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${data.my_confirmed ? 'text-emerald-500' : 'text-gray-300'}`} />
                  <span className="text-sm font-medium text-gray-700">You</span>
                </div>
              </div>
              <div className={`flex-1 p-3 rounded-xl border ${data.partner_confirmed ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${data.partner_confirmed ? 'text-emerald-500' : 'text-gray-300'}`} />
                  <span className="text-sm font-medium text-gray-700">{data.family_member?.name || 'Partner'}</span>
                </div>
              </div>
            </div>

            {!data.my_confirmed && (
              <div className="space-y-3">
                <input
                  type="text" value={reflection} onChange={e => setReflection(e.target.value)}
                  placeholder="How did it go? (optional)"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
                <button
                  data-testid="confirm-family-challenge"
                  onClick={handleConfirm} disabled={confirming}
                  className="w-full py-2.5 rounded-xl text-white text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ background: colors.accent }}
                >
                  {confirming ? 'Confirming...' : 'We Did It!'}
                </button>
              </div>
            )}

            {data.my_confirmed && !data.partner_confirmed && (
              <div className="text-center py-3">
                <p className="text-sm text-gray-500">Waiting for {data.family_member?.name || 'your partner'} to confirm...</p>
              </div>
            )}
          </>
        )}

        {data.both_completed && (
          <div className="text-center p-4 bg-emerald-50 rounded-xl">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold text-emerald-800">Challenge Complete!</p>
            <p className="text-sm text-emerald-600">You both earned {ch.hearts_reward} Hearts</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyChallengeCard;
